import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client/react';
import { apolloClient } from '../client';
import { LOGIN, LOGIN_CODE } from '../graphql/auth';
import SharedButton from '../../components/SharedButton';

function errorMessage(err) {
  return err.graphQLErrors?.[0]?.message || 'An error occurred. Please try again.';
}

export default function LoginPage() {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const [login, { loading: sending }] = useMutation(LOGIN);
  const [loginCode, { loading: verifying }] = useMutation(LOGIN_CODE);

  const completeLogin = async () => {
    await apolloClient.clearStore();
    navigate('/', { replace: true });
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setError('');

    try {
      const { data } = await login({ variables: { email } });
      if (data?.login?.user) {
        await completeLogin();
      } else {
        setStep('code');
      }
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (!code || code.length < 4) return;
    setError('');

    try {
      const { data } = await loginCode({ variables: { email, code } });
      if (data?.loginCode?.user) {
        await completeLogin();
      }
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setCode('');
    setError('');
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <h1 className="login-title">Welcome to AutArch</h1>
        <p className="login-subtitle">
          {step === 'email' ? 'Please sign in to your account' : `We sent a login code to ${email}`}
        </p>

        {error && (
          <div className="login-error" role="alert">
            {error}
          </div>
        )}

        {step === 'email' ? (
          <form onSubmit={handleEmailSubmit}>
            <input
              type="email"
              className="login-input"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
            />
            <SharedButton variant="primary" type="submit" disabled={sending || !email}>
              {sending ? 'Sending...' : 'Login'}
            </SharedButton>
          </form>
        ) : (
          <form onSubmit={handleCodeSubmit}>
            <input
              type="text"
              className="login-input"
              placeholder="Enter 6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              inputMode="numeric"
              maxLength={6}
              required
            />
            <SharedButton variant="primary" type="submit" disabled={verifying || code.length < 4}>
              {verifying ? 'Verifying...' : 'Verify Code'}
            </SharedButton>
            <button type="button" className="login-link" onClick={handleBackToEmail}>
              Use different email
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
